import { Inject, Injectable } from '@angular/core';
import { EcommerceTrackingMapperService } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import {
  eventTrackingMappingConfiguration,
  eventTrackMapper,
  selectUseInternalIds,
} from '@epicuro-next/platform/configuration';
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import {
  EcommerceActionsToTrack,
  IGtmProperties,
  LayerType,
  TrackingService,
} from '@epicuro-next/platform/tracking';
import { loadAccountDetailsSuccess } from '@epicuro-next/state/accounts-state';
import {
  patchSession,
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { loadUserSuccess } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RouterNavigatedAction, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { concatMap, EMPTY, pipe } from 'rxjs';
import { catchError, filter, map, tap, withLatestFrom } from 'rxjs/operators';

import { gtmTrackActions, writeTrackingPayloadToState } from '../actions';

@Injectable()
export class TrackingEffects {
  constructor(
    private trackingService: TrackingService,
    private actions$: Actions,
    private store: Store,
    @Inject(ENVIRONMENT_SETTINGS)
    private environmentSettings: EnvironmentSettings,
    private ecommerceTrackingMapper: EcommerceTrackingMapperService,
  ) {}

  public trackEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(gtmTrackActions.gtmTrackEvent),
        tap(({ action, properties }) => {
          this.trackingService.eventTrack(action, properties);
        }),
      ),
    { dispatch: false },
  );

  public setGTMUserProperty$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(gtmTrackActions.gtmSetUserProperty),
        tap(({ value }) => {
          this.trackingService.setUserProperties(value);
        }),
      ),
    { dispatch: false },
  );

  public loadAccountDetailsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadAccountDetailsSuccess),
        tap(({ account }) => {
          this.trackingService.setAccountId(account.externalId);
          this.trackingService.setUserProperties({
            external_account_id: account.externalId,
          });
        }),
      ),
    { dispatch: false },
  );

  public loadUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserSuccess),
        concatLatestFrom(() => [this.store.select(selectUseInternalIds)]),
        tap(([{ user }, useInternalIds]) => {
          this.trackingService.setUserId(user.userId);
          if (useInternalIds) {
            this.trackingService.setUserProperties({
              user_id: user.externalId,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  public setSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patchSession),
      concatLatestFrom(() => [this.store.pipe(select(selectSiteId))]),
      map(([, siteId]) => {
        let site = siteId;

        if (!site) {
          site = window.location.pathname;
        }

        return gtmTrackActions.gtmSetUserProperty({ value: { site } });
      }),
    ),
  );

  public setSubSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patchSession),
      filter(() => this.environmentSettings.multiSiteSupport === 'true'),
      concatLatestFrom(() => [this.store.pipe(select(selectSubSiteId))]),
      map(([, subSiteId]) => {
        return gtmTrackActions.gtmSetUserProperty({
          value: { subsite: subSiteId ?? '' },
        });
      }),
    ),
  );

  public mapActionsToTrack$ = createEffect(() =>
    this.actions$.pipe(
      withLatestFrom(this.store.select(eventTrackingMappingConfiguration)),
      filter(([_, mappings]) => Boolean(mappings)),
      map(([action, mappings]) => ({
        name: mappings[action.type]?.name,
        eventMappingFunction: mappings[action.type]?.eventMappingFunction,
        payload: { ...action },
      })),
      filter((mappedAction) => Boolean(mappedAction.name)),
      map((mappedAction) =>
        gtmTrackActions.gtmCollectTrackData({
          name: mappedAction.name,
          eventMappingFunction: mappedAction.eventMappingFunction,
          payload: mappedAction.payload,
        }),
      ),
    ),
  );

  /**
   *  Pagetrack effect is a standard effect that needs to be always tracked, only the datamapping of this effect can be different
   */
  public pageTrackEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map((action: RouterNavigatedAction) =>
        gtmTrackActions.gtmCollectTrackData({
          name: EcommerceActionsToTrack.PageTrack,
          payload: action.payload,
        }),
      ),
    ),
  );

  public trackEventLayer1$ = createEffect(
    () => this.actions$.pipe(this.mapAndSendTrackEvent(LayerType.Layer1)),
    { dispatch: false },
  );

  public trackEventLayer2$ = createEffect(
    () => this.actions$.pipe(this.mapAndSendTrackEvent(LayerType.Layer2)),
    { dispatch: false },
  );

  private mapAndSendTrackEvent(layerType: LayerType) {
    return pipe(
      ofType(gtmTrackActions.gtmSendTrackEvent),
      concatLatestFrom((action) =>
        this.store.select(eventTrackMapper(action.name, layerType)),
      ),
      filter(([_, mapper]) => Boolean(mapper)),
      concatMap(([action, mapper]) =>
        this.ecommerceTrackingMapper
          .mapSendTrackEventData(action.name, action.payload, mapper)
          .pipe(
            tap((mappedData: IGtmProperties) => {
              if (mappedData.sendToGCR && mappedData.gcrPayload) {
                this.store.dispatch(
                  writeTrackingPayloadToState({
                    eventName: (mappedData.gcrPayload as { eventType: string })
                      ?.eventType,
                    payload: mappedData.gcrPayload as {},
                  }),
                );
              }
              this.trackingService.sendEventTrack(mappedData, layerType);
            }),
            catchError((err) => this.logError(err)),
          ),
      ),
    );
  }

  private logError(errMessage: unknown) {
    console.error(errMessage);
    return EMPTY;
  }
}
